import classnames from 'classnames';
import { any, find, propEq } from 'ramda';
import { isTruthy } from 'ramda-adjunct';
import React, { FC, ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { slug } from 'to-case';
import { ColorsFilter, FilterByButton, Heading, MenuTitle, Text, TextItemsFilter } from '../../../components/common';
import { ColorIndicator } from '../../../components/common/ColorIndicator';
import { Color } from '../../../constants/Color';
import { FilterName } from '../../../constants/FilterName';
import { mapIndexed } from '../../../utils/array';
import { stopPropagation } from '../../../utils/dom/domEvents';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import styles from './ExtendedItemsFilters.scss';
import { IExtendedFilter, IFilterItem, IOuter } from './ExtendedItemsFilters.types';

const SubTitle: FC = ({ children }) => (
  <Text
    className={styles.subTitle}
    weight="thin"
    size="medium"
    secondary
    dataHook="extended-filters-title"
    tagName="div"
  >
    {children}
  </Text>
);

export const ExtendedItemsFilters: FC<IOuter> = ({
  sourceName,
  title,
  customFilters = [],
  dataHook = 'extended-items-filter',
  isTopFilterVisible,
}) => {
  const { trackSearchAndFiltersFilterClick } = useTrackSearchAndFiltersAnalytics();

  const onFilterByButtonClick = (filterName) => (_event, isOpen) => {
    if (isOpen) {
      trackSearchAndFiltersFilterClick({ target: filterName });
    }
  };

  const messageBoxBody = useMemo(() => document.querySelector('[data-hook="baseModalLayout-content-wrapper"]'), []);

  const [hasVisibleFilters, filtersNode] = useMemo(() => {
    const customFilterNodes = mapIndexed((customFilter: IExtendedFilter, i: number) => {
      const {
        name: filterName,
        activeItemId,
        menuItems = [],
        menuTitle,
        activeItemLabel,
        columnsCount,
        onItemClick,
        options,
      } = customFilter;

      const currentOption = find<IFilterItem>(propEq('id', activeItemId), menuItems);
      const isColorFilter = filterName === FilterName.Color;
      const isColorSelected = activeItemId !== Color.Any;

      return (
        <span key={i} className={styles.filterButton}>
          <FilterByButton
            forSmartGrid
            label={
              activeItemLabel ? (
                isColorFilter ? (
                  <span className={styles.colorFilterLabelWrapper}>
                    {isColorSelected && <ColorIndicator color={activeItemId as Color} />}
                    <span className={styles.colorFilterLabelText}>{activeItemLabel}</span>
                  </span>
                ) : (
                  activeItemLabel
                )
              ) : currentOption ? (
                currentOption.name
              ) : (
                ''
              )
            }
            onClick={onFilterByButtonClick(filterName)}
            dataHook="filter-by-button"
            dataFilterType={slug(filterName)}
            labelStyle={isColorFilter ? { paddingLeft: 9 } : {}}
            menuContent={
              <>
                <MenuTitle text={menuTitle} />

                <div className={styles.itemsWrapper}>
                  {isColorFilter ? (
                    <ColorsFilter
                      isAnyColorFilterVisible={options.isAnyColorFilterVisible}
                      isBlackWhiteFilterVisible={options.isBlackWhiteFilterVisible}
                      customColors={options.customColors}
                      selectedColor={activeItemId as Color}
                      onColorSelect={onItemClick}
                    />
                  ) : (
                    <TextItemsFilter
                      items={menuItems}
                      activeItemId={activeItemId}
                      onClick={onItemClick}
                      columnsCount={columnsCount}
                    />
                  )}
                </div>
              </>
            }
          />
        </span>
      );
    }, customFilters) as ReactNode[];

    return [
      any(isTruthy, customFilterNodes),
      (className) => (
        <div className={classnames(styles.filters, className)}>
          <Text className={styles.filterBy} weight="thin" size="medium" secondary>
            <FormattedMessage id="filters.filter-by" />
          </Text>

          {customFilterNodes}
        </div>
      ),
    ];
  }, [customFilters]);

  return (
    <div className={styles.filtersWrapper} data-hook={dataHook} onClick={stopPropagation}>
      {sourceName && (
        <Heading size="large" dataHook="extended-filters-source-name" className={styles.title}>
          {sourceName}
        </Heading>
      )}

      {title && <SubTitle>{title}</SubTitle>}

      {hasVisibleFilters && (
        <>
          {filtersNode(classnames({ [styles.invisible]: isTopFilterVisible }))}
          {isTopFilterVisible && createPortal(filtersNode(styles.floating), messageBoxBody)}
        </>
      )}
    </div>
  );
};
